type TdpPollingOptions = {
    refreshMs?: number
    retryMs?: number
    // Fires 'online' when the connection comes back; window in the app.
    onlineTarget?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>
}

// Polls the TDP total. A failed fetch (null) never clears the value already
// shown, and is retried sooner than the regular refresh, plus right away when
// the browser comes back online. Returns a stop function.
export function pollTdp<T>(
    fetchTdp: () => Promise<T | null>,
    onValue: (value: T) => void,
    {
        refreshMs = 5 * 60 * 1000,
        retryMs = 30 * 1000,
        onlineTarget = typeof window === 'undefined' ? undefined : window,
    }: TdpPollingOptions = {},
) {
    let stopped = false
    let inFlight = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const run = async () => {
        if (stopped || inFlight) return
        clearTimeout(timer)
        inFlight = true
        const value = await fetchTdp()
        inFlight = false
        if (stopped) return
        if (value) onValue(value)
        timer = setTimeout(run, value ? refreshMs : retryMs)
    }
    const onOnline = () => void run()

    onlineTarget?.addEventListener('online', onOnline)
    void run()

    return () => {
        stopped = true
        clearTimeout(timer)
        onlineTarget?.removeEventListener('online', onOnline)
    }
}
